import React, { useMemo, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';

import { IoMdStats } from 'react-icons/io';
import { BsPersonFill, BsTrash } from 'react-icons/bs';
import { RiAddLine } from 'react-icons/ri';
import { TbBrandDaysCounter, TbFileDownload, TbFolder, TbHomeStats, TbInfoSquareRounded, TbLockAccess, TbUsers } from 'react-icons/tb';
import { FaRegSave, FaTasks } from 'react-icons/fa';
import { PiHandWaving } from 'react-icons/pi';
import { MdOutlineEditCalendar } from 'react-icons/md';
import { LuBellRing } from 'react-icons/lu';
import { AiOutlineAudit } from 'react-icons/ai';

import { selectLoading, setCurrentEntity, setLoading, setModal, setSidenavState } from '../../reducers/app.reducer';
import {
  selectAccount,
  setImpersonated,
  updateAccount,
  selectAllAccounts,
  selectSelectedAccount,
  setSelectedAccount,
  updateRights,
  validateEntity,
  refuseEntity,
  getAllAccounts,
  validateAccount,
  selectActiveAccount,
  updateAllUsers,
  updateUnavailableDays,
} from '../../reducers/account.reducer';
import { selectDocuments, selectRegistries, selectSurveys } from '../../reducers/contentful.reducer';

import { DashboardGeneration } from '../dashboard-generation/DashboardGeneration';
import { DashboardDependencies } from '../dashboard-dependencies/DashboardDependencies';
import { Modal } from '../modal/Modal';
import { Button } from '../Button';
import { DashboardNotes } from '../dashboard-note/DashboardNote';
import { FileExplorer } from '../file-explorer/FileExplorer';
import WeekDaysList from './components/WeekDaysList';
import Reminders from './components/Reminders';
import Audits from './components/Audits';
import Table from '../dashboard-actions/components/Table';
import { AdminUserList } from '../admin-user-list/AdminUserList';
import { AmdinSteps } from '../admin-steps/AdminSteps';

import { isUnavailabilityDaysComplete, isAnySubmittedEntity } from 'src/utils/utils';
import { BUTTON_COLORS, COLORS, COUNTER_TYPES, MODAL_TYPES, USER_ROLES, Z_INDEX } from '../../utils/constants';
import { deleteAccountById, getAccountById, getStats } from '../../utils/api';

import { paths } from 'src/routes/routes.constants';
import { PageWrapper } from '../dashboard-actions/components';

export interface UserDetailsProps {}

export enum UserDetailsType {
  AUTHORIZATIONS = 'surveys',
  USERS = 'users',
  STEPS = 'steps',
  STATS = 'counters',
  VALIDATIONS = 'validations',
  GENERATIONS = 'generations',
  INFOS = 'infos',
  UPLOADS = 'uploads',
  DEPENDENCIES = 'dépendances',
  ACTIONS = 'userActions',
  AGENDA = 'agenda',
  REMINDERS = 'Relances',
  AUDIT = 'audit',
}

export const UserDetails = () => {
  //REDUX
  const dispatch = useDispatch<any>();
  const selectedAccount = useSelector(selectSelectedAccount);
  const activeAccount = useSelector(selectActiveAccount);
  const contentfulDocuments = useSelector(selectDocuments);
  const contentfulRegistries = useSelector(selectRegistries);
  const contentfulSurveys = useSelector(selectSurveys);
  const loading = useSelector(selectLoading);
  const account = useSelector(selectAccount);
  const allAccounts = useSelector(selectAllAccounts);

  const history = useHistory();

  //STATE
  const [currentActionRights, setCurrentActionRights] = useState(selectedAccount?.actionRights);
  const [currentTab, setCurrentTab] = useState(UserDetailsType.INFOS);
  const [currentResponses, setCurrentResponses] = useState(null);
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [modalConfirmation, setModalConfirmation] = useState<{
    title: string;
    process: string;
    entityName: string;
    entityId?: string;
  }>({
    title: '',
    process: '',
    entityName: undefined,
    entityId: undefined,
  });

  const [isolatedChanges, setIsolatedChanges] = useState<{
    legalOfficer?: object;
    links?: object;
    activity?: object;
  }>({});
  const usePrevious = value => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };

  let previousAccount: any | undefined = usePrevious(selectedAccount);

  useEffect(() => {
    if (!selectedAccount) {
      dispatch(
        setSidenavState({
          open: true,
          type: 'admin',
          level: 1,
        })
      );
      return;
    }

    let parseActionRights =
      typeof selectedAccount.actionRights === 'string' ? JSON.parse(selectedAccount.actionRights) : selectedAccount.actionRights;
    setCurrentActionRights(_.cloneDeep(parseActionRights));
    if (previousAccount?._id !== selectedAccount._id) {
      if (account?.accountType === USER_ROLES.AGENCY) {
        handleChangeTab(UserDetailsType.GENERATIONS);
      } else {
        handleChangeTab(UserDetailsType.INFOS);
      }
    }
    setCurrentResponses([...selectedAccount?.entities]);
  }, [selectedAccount]);

  const handleToggleSurvey = async (survey, save) => {
    const newActionRights = _.cloneDeep(currentActionRights);
    if (isSurveyChecked(survey)) {
      newActionRights.surveys = newActionRights.surveys.filter(currentSurvey => currentSurvey.id !== survey.id);
    } else {
      newActionRights.surveys.push({ id: survey.id, label: survey.name ?? survey.label });
    }
    setCurrentActionRights(newActionRights);

    if (save) {
      await saveSelectedAccountRights(newActionRights);
    }
  };

  const handleToggleRegistry = registry => {
    const newActionRights = _.cloneDeep(currentActionRights);
    if (isRegistryChecked(registry)) {
      newActionRights.registries = newActionRights.registries.filter(currentRegistry => currentRegistry.id !== registry.id);
    } else {
      newActionRights.registries.push({ id: registry.id, label: registry.name });
    }
    setCurrentActionRights(newActionRights);
  };

  const getAccountCreationDate = () => {
    if (!selectedAccount) {
      return;
    }
    return new Date(selectedAccount.createdAt).toLocaleDateString();
  };

  const getLegalOfficer = userName => allAccounts.find(account => account.companyName === userName);

  const sortAlphabetically = arr =>
    arr.sort((a, b) => {
      if (a.companyName < b.companyName) {
        return -1;
      }
      if (a.companyName > b.companyName) {
        return 1;
      }
      return 0;
    });

  const getSuperAdmin = () => {
    let admins = sortAlphabetically(allAccounts.filter(account => account.accountType === USER_ROLES.ADMIN));
    return admins.map((adminUser, index) => {
      return (
        <option key={index} value={adminUser.companyName}>
          {adminUser.companyName}
        </option>
      );
    });
  };

  const handleChangeTab = tab => {
    setCurrentTab(tab);
  };

  const handleEntityRejection = async entityId => {
    dispatch(setLoading(true));
    dispatch(refuseEntity({ adminId: account._id, accountId: selectedAccount._id, entityId: entityId, reason: '' }));
    dispatch(setLoading(false));
  };

  const handleEntityValidation = async entityId => {
    dispatch(setLoading(true));
    dispatch(validateEntity({ adminId: account._id, accountId: selectedAccount._id, entityId: entityId }));
    dispatch(setLoading(false));
  };

  const handleSaveStats = async () => {
    dispatch(setLoading(true));
    // dispatch(updateStats());
    dispatch(setLoading(false));
  };

  const handleToggleDocument = docx => {
    const newActionRights = _.cloneDeep(currentActionRights);
    if (isDocumentChecked(docx)) {
      newActionRights.documents = newActionRights.documents.filter(currentDocx => currentDocx.id !== docx.id);
    } else {
      newActionRights.documents.push({ id: docx.id, label: docx.name });
    }
    setCurrentActionRights(newActionRights);
  };

  const handleAccountInfosChange = (e, isBoolean: boolean = false) => {
    if (e.target.value === selectedAccount[e.target.name]) {
      const newIsolatedChanges = _.cloneDeep(isolatedChanges);
      delete newIsolatedChanges[e.target.name];
      setIsolatedChanges(newIsolatedChanges);
      return;
    }
    setIsolatedChanges({ ...isolatedChanges, [e.target.name]: e.target.value });
  };

  const handleAccountValidation = async () => {
    dispatch(validateAccount({ accountId: selectedAccount._id }));
  };

  const handleUserLegalOfficerChange = e => {
    if (e.target.value === 'Sélectionner...' || e.target.value === selectedAccount.legalOfficer?.name) {
      const newIsolatedChanges = _.cloneDeep(isolatedChanges);
      delete newIsolatedChanges[e.target.name];
      setIsolatedChanges(newIsolatedChanges);
      return;
    }
    let legalOfficer = getLegalOfficer(e.target.value);

    setIsolatedChanges({
      ...isolatedChanges,
      legalOfficer: {
        name: legalOfficer.companyName,
        email: legalOfficer.users[0].email,
      },
    });
  };

  const isDocumentChecked = docx => {
    if (!currentActionRights) {
      return false;
    }
    return currentActionRights.documents.some(currentDocx => currentDocx.id === docx.id);
  };

  const isRegistryChecked = registry => {
    if (!currentActionRights) {
      return false;
    }
    return currentActionRights.registries.some(currentRegistry => currentRegistry.id === registry.id);
  };

  const isSurveyChecked = survey => {
    if (!currentActionRights) {
      return false;
    }
    return currentActionRights.surveys.some(currentSurvey => currentSurvey.id === survey.id);
  };

  const saveSelectedAccountInfos = async () => {
    let isLegalOfficerInitialization = !hasLegalOfficer && isolatedChanges.hasOwnProperty('legalOfficer');

    await dispatch(
      updateAccount({
        accountId: selectedAccount._id,
        infosChanges: isolatedChanges,
        legalOfficerInitialization: isLegalOfficerInitialization,
      })
    );
    setIsolatedChanges({});
  };

  const saveSelectedAccountRights = async (newActionRights = false) => {
    dispatch(
      updateRights({
        accountId: selectedAccount._id,
        actionRights: newActionRights ? newActionRights : currentActionRights,
      })
    );
  };

  const updateCurrentEntityCounterById = (counterId, amount, targetEntity) => {
    const newUserResponses = _.cloneDeep(currentResponses);
    const entity = newUserResponses.find(entity => entity.name === targetEntity.name);
    const counter = entity.counters.find(counter => counter.id === counterId);

    if (counter) {
      counter.amount = parseInt(amount);
      let currentCounterIndex = entity.counters.findIndex(counter => counter.id === counterId);
      let newCounterIndex = COUNTER_TYPES.findIndex(counter => counter.id === counterId);
      if (currentCounterIndex !== newCounterIndex) {
        let changedCounter = entity.counters.splice(currentCounterIndex, 1)[0];
        entity.counters.splice(newCounterIndex, 0, changedCounter);
      }
    } else {
      let newCounter = COUNTER_TYPES.find(counter => counter.id === counterId);
      let newCounterIndex = COUNTER_TYPES.findIndex(counter => counter.id === counterId);
      newCounter.amount = parseInt(amount);
      entity.counters.splice(newCounterIndex, 0, newCounter);
    }
    setCurrentResponses(newUserResponses);
  };

  const handleConfirmationModalValidation = async () => {
    switch (modalConfirmation.process) {
      case 'entity_validation':
        await handleEntityValidation(modalConfirmation.entityId);
        break;
      case 'entity_rejection':
        await handleEntityRejection(modalConfirmation.entityId);
        break;
      case 'account_validation':
        await handleAccountValidation();
        break;
    }
    closeConfirmationModal(null);
  };

  const closeConfirmationModal = e => {
    e?.stopPropagation();
    setModalConfirmation({ title: '', process: '', entityName: undefined });
    setShowModalConfirmation(false);
  };

  const handleAddUser = async () => {
    await dispatch(setModal({ show: true, type: MODAL_TYPES.ADD_USER }));
  };

  const handleImpersonate = async account => {
    const accountRefreshedData = await getAccountById(account._id);
    dispatch(setImpersonated(accountRefreshedData));
    dispatch(setCurrentEntity(null));
    history.push(paths.dashboardGroup);
  };

  const handleDeleteAccount = async accountId => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce compte ?') === true) {
      await deleteAccountById(accountId);
      dispatch(getAllAccounts());
      dispatch(setSelectedAccount(null));
      toast.error('Le client a bien été supprimé', { position: 'top-center' });
    }

    dispatch(setLoading(false));
  };

  const handleUpdateAllUsers = async () => {
    dispatch(updateAllUsers({ accountId: selectedAccount._id, users: selectedAccount.users }));
    toast.info(`les infos des utilisateurs ont bien été mis à jour !`);
  };

  const hasLegalOfficer = selectedAccount?.legalOfficer?.name !== undefined && selectedAccount?.legalOfficer?.name !== '';

  // ---------------------------------------------

  const [unavailabilityDays, setUnavailabilityDays] = useState(
    activeAccount?.unavailability || [
      { day: 1, startTime: '', endTime: '', allDay: false },
      { day: 2, startTime: '', endTime: '', allDay: false },
      { day: 3, startTime: '', endTime: '', allDay: false },
      { day: 4, startTime: '', endTime: '', allDay: false },
      { day: 5, startTime: '', endTime: '', allDay: false },
    ]
  );

  const isAllSlotsValid =
    useMemo(() => isUnavailabilityDaysComplete(unavailabilityDays), [unavailabilityDays]) &&
    JSON.stringify(activeAccount?.unavailability) !== JSON.stringify(unavailabilityDays);

  const saveLegalOfficerUnavailability = async () => {
    let newSelectedAccount = _.cloneDeep(selectedAccount);
    newSelectedAccount.unavailability = unavailabilityDays;
    dispatch(
      updateUnavailableDays({
        accountId: newSelectedAccount._id,
        unavailabilityDays,
      })
    );
  };

  const handleGetStats = async () => {
    dispatch(setLoading(true));
    let statsBuffer = await getStats();
    if (statsBuffer === false) {
      dispatch(setLoading(false));
      return;
    }
    let blob = new Blob([new Uint8Array(statsBuffer.data)], { type: 'application/octet-stream' });
    let currentDate = new Date().toLocaleDateString();
    dispatch(setLoading(false));
    saveAs(blob, `DipeeoStats-${currentDate}.xlsx`);
  };

  const getMissingSurveys = () => {
    let missingSurveys = [];
    let CTFsurveys = contentfulSurveys?.map(survey => survey.id);

    if (CTFsurveys) {
      currentActionRights?.surveys.forEach(survey => {
        if (!CTFsurveys.includes(survey.id)) {
          missingSurveys.push(survey);
        }
      });
    }
    return missingSurveys;
  };

  //TODO: make components for each admin feature to make this file clearer
  return (
    <>
      {selectedAccount ? (
        <UserDetailsContainer>
          <TabContainer>
            <TabSection expand={true}>
              {account?.accountType !== USER_ROLES.AGENCY && (
                <>
                  <Tab current={currentTab === UserDetailsType.INFOS} onClick={() => handleChangeTab(UserDetailsType.INFOS)}>
                    <TbInfoSquareRounded />
                    <span>Infos</span>
                  </Tab>
                  <Tab current={currentTab === UserDetailsType.USERS} onClick={() => handleChangeTab(UserDetailsType.USERS)}>
                    <TbUsers />
                    <span>Utilisateurs</span>
                  </Tab>
                  <Tab current={currentTab === UserDetailsType.AUTHORIZATIONS} onClick={() => handleChangeTab(UserDetailsType.AUTHORIZATIONS)}>
                    <TbLockAccess />
                    <Infos>
                      <span>Autorisations</span>
                      {getMissingSurveys().length > 0 && <PiHandWaving />}
                    </Infos>
                  </Tab>
                  <Tab current={currentTab === UserDetailsType.ACTIONS} onClick={() => handleChangeTab(UserDetailsType.ACTIONS)}>
                    <FaTasks />
                    <span>Plan d'actions</span>
                  </Tab>
                  <Tab current={currentTab === UserDetailsType.AUDIT} onClick={() => handleChangeTab(UserDetailsType.AUDIT)}>
                    <AiOutlineAudit />
                    <span>Audits</span>
                  </Tab>
                  <Tab current={currentTab === UserDetailsType.STEPS} onClick={() => handleChangeTab(UserDetailsType.STEPS)}>
                    <TbBrandDaysCounter />
                    <Infos>
                      <span>Étapes</span>
                      {isAnySubmittedEntity(account) && <PiHandWaving />}
                    </Infos>
                  </Tab>
                  <Tab current={currentTab === UserDetailsType.STATS} onClick={() => handleChangeTab(UserDetailsType.STATS)}>
                    <TbHomeStats />
                    <span>Stats</span>
                  </Tab>
                  <Tab current={currentTab === UserDetailsType.UPLOADS} onClick={() => handleChangeTab(UserDetailsType.UPLOADS)}>
                    <TbFolder />
                    <span>Upload / Download</span>
                  </Tab>
                  {selectedAccount._id === activeAccount._id && (
                    <Tab current={currentTab === UserDetailsType.AGENDA} onClick={() => handleChangeTab(UserDetailsType.AGENDA)}>
                      <MdOutlineEditCalendar />
                      Agenda
                    </Tab>
                  )}
                </>
              )}
              <Tab current={currentTab === UserDetailsType.GENERATIONS} onClick={() => handleChangeTab(UserDetailsType.GENERATIONS)}>
                <TbFileDownload />
                <span>Générations</span>
              </Tab>
            </TabSection>
            <TabSection>
              <Tab current={currentTab === UserDetailsType.REMINDERS} onClick={() => handleChangeTab(UserDetailsType.REMINDERS)}>
                <LuBellRing />
                {UserDetailsType.REMINDERS}
              </Tab>
              {account?.accountType !== USER_ROLES.AGENCY && (
                <>
                  <Tab onClick={handleGetStats}>
                    {' '}
                    <IoMdStats />
                    Statistiques
                  </Tab>
                  <Tab current={currentTab === UserDetailsType.DEPENDENCIES} onClick={() => handleChangeTab(UserDetailsType.DEPENDENCIES)}>
                    Dépendances
                  </Tab>
                </>
              )}
            </TabSection>
          </TabContainer>
          <ContentContainer>
            <FlexContainer>
              <span>{selectedAccount?.companyName}</span>
              <Since>Client depuis le {getAccountCreationDate()}</Since>
              <Button label={'Impersonate'} type={'admin'} icon={BsPersonFill} onClick={() => handleImpersonate(selectedAccount)} />
            </FlexContainer>
            {(currentTab === UserDetailsType.INFOS ||
              currentTab === UserDetailsType.USERS ||
              currentTab === UserDetailsType.AUTHORIZATIONS ||
              currentTab === UserDetailsType.AGENDA ||
              currentTab === UserDetailsType.STATS) && (
              <UserDetailsTop>
                <UserActionContainer>
                  {currentTab === UserDetailsType.USERS && (
                    <>
                      <Button label={'Ajouter un administrateur'} icon={RiAddLine} onClick={handleAddUser} />
                      <Button label={'sauvegarder les modifications'} icon={FaRegSave} onClick={handleUpdateAllUsers} type={'success'} />
                    </>
                  )}
                  {currentTab === UserDetailsType.INFOS && (
                    <>
                      <Button label={'Supprimer'} type={'danger'} icon={BsTrash} onClick={() => handleDeleteAccount(selectedAccount?._id)} />
                      <Button
                        icon={FaRegSave}
                        label={'Mettre à jour les infos'}
                        onClick={saveSelectedAccountInfos}
                        customColor={BUTTON_COLORS.default}
                        disabled={JSON.stringify(isolatedChanges) === '{}'}
                      />
                    </>
                  )}
                  {account?.accountType !== USER_ROLES.AGENCY && currentTab === UserDetailsType.AUTHORIZATIONS && (
                    <Button type={'success'} onClick={() => saveSelectedAccountRights()} label={'Mettre à jour les autorisations'} />
                  )}
                  {currentTab === UserDetailsType.STATS && (
                    <Button type={'success'} onClick={handleSaveStats} label={'Mettre à jour les compteurs'} />
                  )}
                  {currentTab === UserDetailsType.AGENDA && (
                    <Button
                      icon={FaRegSave}
                      label={'Mettre à jour les infos'}
                      onClick={saveLegalOfficerUnavailability}
                      type={'success'}
                      disabled={!isAllSlotsValid}
                    />
                  )}
                </UserActionContainer>
              </UserDetailsTop>
            )}
            {!loading && (
              <Content>
                {currentTab === UserDetailsType.INFOS && (
                  <>
                    <InfosFlexContainer>
                      <Left>
                        <UserInfosSlider>
                          <InfoItem>
                            <span>Nom </span>
                            <input
                              defaultValue={selectedAccount?.companyName}
                              name={'companyName'}
                              onChange={handleAccountInfosChange}
                              placeholder={'nom complet du groupe'}
                              type='text'
                            />
                          </InfoItem>
                          <InfoItem>
                            <span>Type de compte</span>
                            {account?.accountType === USER_ROLES.ADMIN && (
                              <select defaultValue={selectedAccount.accountType} onChange={handleAccountInfosChange} name='accountType' id=''>
                                {Object.keys(USER_ROLES).map((type, index) => {
                                  return (
                                    <option key={index} value={USER_ROLES[type]}>
                                      {USER_ROLES[type]}
                                    </option>
                                  );
                                })}
                              </select>
                            )}
                          </InfoItem>
                          <InfoItem>
                            <span>Responsable juridique</span>
                            {account?.accountType === USER_ROLES.ADMIN && (
                              <select
                                defaultValue={selectedAccount?.legalOfficer?.name}
                                onChange={handleUserLegalOfficerChange}
                                name='legalOfficer'
                                id=''>
                                <option>Sélectionner...</option>
                                {getSuperAdmin()}
                              </select>
                            )}
                            {!hasLegalOfficer && <ErrorMessage>Aucun responsable juridique n'est attribué</ErrorMessage>}
                          </InfoItem>
                          <InfoItem>
                            <span>L'utilisateur a un produit : </span>
                            <div>
                              <label htmlFor={'with_product_true'}>
                                <input
                                  defaultChecked={selectedAccount.withProduct === 'yes' || selectedAccount.withProduct === 'true'}
                                  name={'withProduct'}
                                  onChange={e => handleAccountInfosChange(e)}
                                  type='radio'
                                  id={'with_product_true'}
                                  value={'yes'}
                                />
                                Oui
                              </label>
                              <label htmlFor={'with_product_false'}>
                                <input
                                  defaultChecked={selectedAccount.withProduct === 'no' || selectedAccount.withProduct === 'false'}
                                  name={'withProduct'}
                                  onChange={e => handleAccountInfosChange(e)}
                                  type='radio'
                                  id={'with_product_false'}
                                  value={'no'}
                                />
                                Non
                              </label>
                              <label htmlFor={'with_product_idk'}>
                                <input
                                  defaultChecked={selectedAccount.withProduct === 'idk' || !selectedAccount.hasOwnProperty('withProduct')}
                                  name={'withProduct'}
                                  onChange={e => handleAccountInfosChange(e)}
                                  type='radio'
                                  id={'with_product_idk'}
                                  value={'idk'}
                                />
                                Je ne sais pas
                              </label>
                            </div>
                          </InfoItem>
                          <InfoItem>
                            <span>L’utilisateur a acheté des CGU/CGV : </span>
                            <div>
                              <label htmlFor={'cgu_true'}>
                                <input
                                  defaultChecked={selectedAccount.cgu === 'yes' || selectedAccount.cgu === 'true'}
                                  name={'cgu'}
                                  onChange={e => handleAccountInfosChange(e)}
                                  type='radio'
                                  id={'cgu_true'}
                                  value={'yes'}
                                />
                                Oui
                              </label>
                              <label htmlFor={'cgu_false'}>
                                <input
                                  defaultChecked={selectedAccount.cgu === 'no' || selectedAccount.cgu === 'false'}
                                  name={'cgu'}
                                  onChange={e => handleAccountInfosChange(e)}
                                  type='radio'
                                  id={'cgu_false'}
                                  value={'no'}
                                />
                                Non
                              </label>
                              <label htmlFor={'cgu_idk'}>
                                <input
                                  defaultChecked={selectedAccount.cgu === 'idk' || !selectedAccount.hasOwnProperty('cgu')}
                                  name={'cgu'}
                                  onChange={e => handleAccountInfosChange(e)}
                                  type='radio'
                                  id={'cgu_idk'}
                                  value={'idk'}
                                />
                                Je ne sais pas
                              </label>
                            </div>
                          </InfoItem>
                          <InfoItem>
                            <span>L'utilisateur est un sous-traitant :</span>
                            <div>
                              <label htmlFor={'processor_true'}>
                                <input
                                  defaultChecked={selectedAccount.processor === 'yes' || selectedAccount.processor === 'true'}
                                  name={'processor'}
                                  onChange={e => handleAccountInfosChange(e)}
                                  type='radio'
                                  id={'processor_true'}
                                  value={'yes'}
                                />
                                Oui
                              </label>
                              <label htmlFor={'processor_false'}>
                                <input
                                  defaultChecked={selectedAccount.processor === 'no' || selectedAccount.withProduct === 'false'}
                                  name={'processor'}
                                  onChange={e => handleAccountInfosChange(e)}
                                  type='radio'
                                  id={'processor_false'}
                                  value={'no'}
                                />
                                Non
                              </label>
                              <label htmlFor={'processor_idk'}>
                                <input
                                  defaultChecked={selectedAccount.processor === 'idk' || !selectedAccount.hasOwnProperty('processor')}
                                  name={'processor'}
                                  onChange={e => handleAccountInfosChange(e)}
                                  type='radio'
                                  id={'processor_idk'}
                                  value='idk'
                                />
                                Je ne sais pas
                              </label>
                            </div>
                          </InfoItem>
                          <InfoItem>
                            <span>Identifiant de référence Pennylane</span>
                            <input
                              defaultValue={selectedAccount?.pennylaneReference}
                              name={'pennylaneReference'}
                              onChange={handleAccountInfosChange}
                              placeholder={'Identifiant de référence Pennylane'}
                              type='text'
                            />
                          </InfoItem>
                        </UserInfosSlider>
                      </Left>
                      <Right>
                        <DashboardNotes
                          style={{
                            display: 'flex',
                            height: 'calc(100% - 10px)',
                            flexDirection: 'column',
                          }}
                        />
                      </Right>
                    </InfosFlexContainer>
                  </>
                )}
                {currentTab === UserDetailsType.USERS && <AdminUserList />}
                {account?.accountType !== USER_ROLES.AGENCY && currentTab === UserDetailsType.AUTHORIZATIONS && (
                  <RightsContainer>
                    <SectionContainer>
                      <List>
                        {getMissingSurveys().length > 0 && <ListTitle>Questionnaires manquants sur CTF</ListTitle>}
                        {getMissingSurveys().map((survey, index) => (
                          <React.Fragment key={`${survey.label}-${index}`}>
                            <label className={'missing'} htmlFor={`surveyID-${survey.id}`}>
                              <input
                                checked={isSurveyChecked(survey)}
                                type='checkbox'
                                name={`survey-${survey.id}`}
                                id={`surveyID-${survey.id}`}
                                onChange={() => handleToggleSurvey(survey, true)}
                              />
                              {survey.label}
                            </label>
                          </React.Fragment>
                        ))}
                        <ListTitle>Questionnaires autorisés</ListTitle>
                        {contentfulSurveys.map((survey, index) => {
                          return (
                            <React.Fragment key={`${survey.name}-${index}`}>
                              <label htmlFor={`surveyID-${survey.id}`}>
                                <input
                                  checked={isSurveyChecked(survey)}
                                  type='checkbox'
                                  name={`survey-${survey.id}`}
                                  id={`surveyID-${survey.id}`}
                                  onChange={() => handleToggleSurvey(survey, false)}
                                />
                                {survey.name}
                              </label>
                            </React.Fragment>
                          );
                        })}
                      </List>
                      <List>
                        <ListTitle>Documents authorisés</ListTitle>
                        {contentfulDocuments.map((document, index) => {
                          return (
                            <React.Fragment key={`${document.name}-${index}`}>
                              <label htmlFor={`surveyID-${document.id}`}>
                                <input
                                  checked={isDocumentChecked(document)}
                                  type='checkbox'
                                  name={`survey-${document.id}`}
                                  id={`surveyID-${document.id}`}
                                  onChange={() => handleToggleDocument(document)}
                                />
                                {document.name}
                              </label>
                            </React.Fragment>
                          );
                        })}
                      </List>
                      {contentfulRegistries.length > 0 && (
                        <List>
                          <ListTitle>Registres autorisés</ListTitle>
                          {contentfulRegistries.map((registry, index) => {
                            return (
                              <React.Fragment key={`${registry.name}-${index}`}>
                                <label htmlFor={`surveyID-${registry.id}`}>
                                  <input
                                    checked={isRegistryChecked(registry)}
                                    type='checkbox'
                                    name={`survey-${registry.id}`}
                                    id={`surveyID-${registry.id}`}
                                    onChange={() => handleToggleRegistry(registry)}
                                  />
                                  {registry.name}
                                </label>
                              </React.Fragment>
                            );
                          })}
                        </List>
                      )}
                    </SectionContainer>
                  </RightsContainer>
                )}
                {account?.accountType !== USER_ROLES.AGENCY && currentTab === UserDetailsType.ACTIONS && (
                  <PageWrapper admin>
                    <Table admin />
                  </PageWrapper>
                )}
                {currentTab === UserDetailsType.STEPS && <AmdinSteps />}
                {currentTab === UserDetailsType.STATS && (
                  <GridContainer>
                    {currentResponses?.map((entity, index) => {
                      return (
                        <List key={index}>
                          <ListTitle>Compteurs pour {entity.name}</ListTitle>
                          <Counters>
                            <label htmlFor='assistance_requests'>
                              <div>Demandes d'assistance</div>
                              <input
                                defaultValue={entity.counters.find(counters => counters.id === 'assistance_requests')?.amount}
                                onChange={e => updateCurrentEntityCounterById('assistance_requests', e.target.value, entity)}
                                name={'assistance_requests'}
                                type='text'
                              />
                            </label>
                            <label htmlFor='user_rights'>
                              <div>Droits des personnes</div>
                              <input
                                defaultValue={entity.counters.find(counters => counters.id === 'user_rights')?.amount}
                                onChange={e => updateCurrentEntityCounterById('user_rights', e.target.value, entity)}
                                name={'user_rights'}
                                type='text'
                              />
                            </label>
                            <label htmlFor='data_violation'>
                              <div>Violations de données</div>
                              <input
                                defaultValue={entity.counters.find(counters => counters.id === 'data_violation')?.amount}
                                onChange={e => updateCurrentEntityCounterById('data_violation', e.target.value, entity)}
                                name={'data_violation'}
                                type='text'
                              />
                            </label>
                          </Counters>
                        </List>
                      );
                    })}
                  </GridContainer>
                )}
                {currentTab === UserDetailsType.AGENDA && (
                  <>
                    <InfosFlexContainer>
                      <Left>
                        <UserInfosSlider>
                          <InfoItem>
                            <span style={{ marginBottom: '1rem' }}>Indiquez vos indisponibilités</span>
                            <WeekDaysList unavailabilityDays={unavailabilityDays} setUnavailabilityDays={setUnavailabilityDays} />
                          </InfoItem>
                          <div className='spacer'>&nbsp;</div>
                        </UserInfosSlider>
                      </Left>
                    </InfosFlexContainer>
                  </>
                )}
                {currentTab === UserDetailsType.DEPENDENCIES && <DashboardDependencies></DashboardDependencies>}
                {currentTab === UserDetailsType.GENERATIONS && <DashboardGeneration></DashboardGeneration>}
                {currentTab === UserDetailsType.UPLOADS && <FileExplorer admin={true} />}
                {currentTab === UserDetailsType.REMINDERS && <Reminders />}
                {currentTab === UserDetailsType.AUDIT && <Audits />}
              </Content>
            )}
          </ContentContainer>
          <Modal onClose={() => closeConfirmationModal(null)} show={showModalConfirmation} title={'Confirmation'}>
            <ConfirmationModalContainer>
              <ModalText>{modalConfirmation.title}</ModalText>
              <ConfirmationActions>
                <Action validation={false} onClick={closeConfirmationModal}>
                  <Label>Annuler</Label>
                </Action>
                <Action validation={true} onClick={handleConfirmationModalValidation}>
                  <Label>Valider</Label>
                </Action>
              </ConfirmationActions>
              {/*<ErrorMessage dangerouslySetInnerHTML={{__html:errors?.message}}/>*/}
            </ConfirmationModalContainer>
          </Modal>
        </UserDetailsContainer>
      ) : (
        <NoAccountMessage>Veuillez sélectionner le compte client que vous souhaitez consulter pour afficher les détails.</NoAccountMessage>
      )}
    </>
  );
};

const NoAccountMessage = styled.h1`
  align-self: center;
  width: 100%;
  text-align: center;
  color: black;
  margin: 0 20% 0 calc(20% + 360px);
`;

const Infos = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;

  svg {
    color: ${COLORS.Squash};
    animation: swing 0.8s infinite ease-in-out;
    height: 20px;
    width: 20px;
  }
`;

const TabSection = styled.div<{ expand?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;

  > div:first-child {
    border-top: 1px solid ${COLORS.MediumGrey};
  }

  &:first-child {
    > div:first-child {
      border-top: none;
    }
  }

  ${({ expand }) =>
    expand &&
    css`
      flex: 1;
    `};
`;

const UserActionContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
  width: 100%;
  justify-content: flex-end;
  padding: 20px 0;
`;

const TabContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 2px;
  flex-direction: column;
  border-right: 1px solid #e9edf7;
  overflow-y: auto;
`;

const Tab = styled.div<{ current?: boolean }>`
  //height: 100%;
  padding: 20px 15px;
  width: calc(100% - 30px);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  transition: all 0.2s ease;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${COLORS.MediumGrey};
  font-size: 1.125rem;
  column-gap: 5px;

  &:hover {
    background-color: #187ead;
    color: #fff;
  }

  ${props =>
    props.current &&
    `
         background-color: #187ead;
         color: #fff;
  `}
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Action = styled.div<{ validation: boolean }>`
  cursor: pointer;
  font-family: 'Roboto';
  font-weight: 500;
  font-size: 1.2em;
  transition: all 0.2s;
  //border: 1px solid transparent;
  //border-radius: 10px;
  //padding: 10px;
  //background-color: ${({ validation }) => (validation ? '#82d0a5' : validation === false ? '#ff5c5c' : '#187EAD')};
  background-color: white;
  color: black;
  text-transform: uppercase;

  //&:hover {
  //  color: #187EAD;
  //  border-color: #187EAD;
  //  background-color: transparent;
  //}
`;

const Counters = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
`;

const ListTitle = styled.div`
  font-size: 1.3em;
  font-weight: 500;
  color: #187ead;
  border-bottom: 1px solid #e9edf7;
  margin-bottom: 10px;
  width: max-content;
`;

const Since = styled.div`
  font-size: 1em;
  color: #a3aed0;
  flex: 1;
`;

const RightsContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 40px;
  height: 100%;
  width: 100%;
`;

const FlexContainer = styled.div`
  display: flex;
  grid-template-columns: 1fr 1fr;
  width: calc(100% - 40px);
  align-items: center;
  column-gap: 10px;
  padding: 15px 20px;
  border-bottom: 1px solid ${COLORS.MediumGrey};
  height: 30px;

  span {
    font-size: 1.5em;
  }
`;

const GridContainer = styled.div`
  display: flex;
  column-gap: 20px;
  row-gap: 15px;
  //height: 100%;
  //width: 100%;
  flex: 1;
  grid-template-columns: repeat(auto-fill, minmax(calc(50% - 10px), 1fr));
  grid-auto-flow: dense;
`;

const InfosFlexContainer = styled.div`
  display: flex;
  column-gap: 30px;
  height: 100%;
`;

const Right = styled.div`
  flex: 1;
  height: calc(100% - 62px);
  display: flex;
  flex-direction: column;
  row-gap: 20px;
`;

const Left = styled.div`
  flex: 3;
  overflow-y: auto;
`;

const UserInfosSlider = styled.div`
  height: calc(100% - 40px);
  display: grid;
  flex-direction: column;
  row-gap: 20px;
  column-gap: 20px;
  padding: 20px 20px;
  grid-template-columns: 1fr 1fr;

  .spacer {
    height: 20px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 3px;
  flex: 1;

  > label {
    display: flex;
    align-items: center;
    column-gap: 10px;
    cursor: pointer;

    > input {
      margin: 0;
    }

    &.missing {
      color: red;
    }
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  column-gap: 10px;
  grid-row: span 1;

  div {
    display: flex;
    column-gap: 10px;

    select {
      flex: 1;
    }

    button {
      min-width: 0px;
    }
  }

  input,
  select {
    padding: 5px 10px;
    width: fit-content;
    border: 1px solid lightgray;
    border-radius: 5px;
    margin: 0;
    width: auto;

    &:focus-visible {
      border: 1px solid #187ead;
      outline: none;
    }
  }

  > span {
    font-size: 1.125em;
    color: #000;
    font-weight: bold;
  }

  label {
    display: flex;
    align-items: center;
    column-gap: 5px;
    font-size: 1.125em;
  }
`;

const SectionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 20px;
  row-gap: 20px;
  width: 100%;
  height: 100%;

  label {
    display: flex;
    align-items: center;
  }

  label div {
    width: 140px;
  }

  label input {
    margin-right: 10px;
    margin-left: 10px;
  }
`;

const Content = styled.div`
  padding: 20px;
  width: calc(100% - 40px);
  display: flex;
  column-gap: 15px;
  height: calc(100% - 102px);
  flex-direction: column;
`;

const UserDetailsContainer = styled.div`
  background-color: white;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  z-index: ${Z_INDEX.above};
`;

const UserDetailsTop = styled.div`
  display: flex;
  align-items: center;
  padding: 0 20px;
  height: 60px;
  min-height: 60px;
  max-height: 60px;
  width: calc(100% - 40px);
  color: black;
  column-gap: 20px;
  justify-content: space-between;
`;

const ConfirmationModalContainer = styled.div`
  //display: flex;
  //flex-direction: column;
  //row-gap: 20px;
  //padding: 20px 40px;
  //font-size: 1.3em;
`;

const ModalText = styled.div`
  font-size: 1.5em;
`;

const Label = styled.span``;

const ConfirmationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  column-gap: 35px;
  padding-top: 30px;
`;

const ErrorMessage = styled.div`
  color: red;
`;
